"use client";

/**
 * TRPCProvider — wraps the app with TanStack QueryClient + tRPC provider.
 *
 * Wave 6 production readiness: this provider is mounted once in the root
 * `app/layout.tsx` so every server-rendered route gets a fresh React Query
 * client per request, and the tRPC react hooks (`trpc.*.useQuery`) work
 * from any client component.
 */

import * as React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { trpc, makeTrpcClient } from "./trpc-client";

export function TRPCProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // 30s matches the Wave 3 proposals polling cadence.
            staleTime: 30_000,
            refetchOnWindowFocus: false,
          },
        },
      }),
  );
  const [trpcClient] = React.useState(() => makeTrpcClient());

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
}
